import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { Search, Plus, MoreVertical, Trash2, Users } from 'lucide-react';
import { useConversations } from '../../hooks/useConversations';
import { useAuth } from '../../hooks/useAuth';
import { Avatar } from '../../components/common/Avatar';
import { supabase } from '../../lib/supabase';
import { Modal } from '../../components/common/Modal';
import { useTranslation } from '../../hooks/useTranslation';

interface Participant {
  user_id: string;
  profile: {
    username: string;
    avatar_url?: string;
  };
}

interface Conversation {
  id: string;
  created_at: string;
  participants: {
    user_id: string;
    unread_count: number;
    profile: {
      username: string;
      avatar_url?: string;
    };
  }[];
  last_message?: {
    content: string;
    created_at: string;
  };
}

export const ConversationsList = () => {
  const { id: activeId } = useParams<{ id: string }>();
  const { conversations, loading, fetchConversations } = useConversations();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [conversationToDelete, setConversationToDelete] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, getPathForLang } = useTranslation();

  // Rafraîchir la liste quand on revient à la liste des conversations
  useEffect(() => {
    if (!location.pathname.includes('/new')) {
      fetchConversations();
    }
  }, [location.pathname]);

  const deleteConversation = async (conversationId: string) => {
    try {
      // 1. Supprimer les notifications liées à la conversation
      const { error: notificationsError } = await supabase
        .from('notifications')
        .delete()
        .eq('conversation_id', conversationId);

      if (notificationsError) throw notificationsError;

      // 2. Supprimer les entrées de présence
      const { error: presenceError } = await supabase
        .from('presence')
        .delete()
        .eq('conversation_id', conversationId);

      if (presenceError) throw presenceError;

      // 3. Supprimer les messages privés
      const { error: messagesError } = await supabase
        .from('private_messages')
        .delete()
        .eq('conversation_id', conversationId);

      if (messagesError) throw messagesError;

      // 4. Supprimer les participants
      const { error: participantsError } = await supabase
        .from('conversation_participants')
        .delete()
        .eq('conversation_id', conversationId);

      if (participantsError) throw participantsError;

      // 5. Enfin, supprimer la conversation
      const { error: conversationError } = await supabase
        .from('conversations')
        .delete()
        .eq('id', conversationId);

      if (conversationError) throw conversationError;

      // Si la conversation supprimée est celle qui est affichée, rediriger vers la liste
      if (conversationId === activeId) {
        navigate('/messages');
      }

      // Rafraîchir la liste et fermer la modal
      fetchConversations();
      setConversationToDelete(null);
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  const getOtherParticipant = (conversation: Conversation) => {
    if (!user) return null;
    
    const otherParticipant = conversation.participants.find(
      p => p.user_id !== user.id
    );
    
    if (!otherParticipant) {
      console.warn('No other participant found in conversation:', conversation);
      return null;
    }
    
    return otherParticipant.profile;
  };

  console.log('Conversations:', conversations);

  const filteredConversations = conversations.filter((conversation) => {
    const otherUser = getOtherParticipant(conversation);
    return otherUser?.username.toLowerCase().includes(searchTerm.toLowerCase());
  });

  console.log('Filtered Conversations:', filteredConversations);

  return (
    <>
      <div className="flex flex-col h-full">
        {/* En-tête avec bouton nouveau message */}
        <div className="sticky top-0 bg-white z-10">
          <div className="p-3 md:p-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold">{t('messages.title')}</h2>
            <Link 
              to={getPathForLang('/messages/new')}
              className="p-2 text-gray-600 hover:text-primary-600 transition-colors"
              aria-label={t('messages.new_message_aria')}
            >
              <Plus className="w-5 h-5" />
            </Link>
          </div>

          {/* Barre de recherche */}
          <div className="p-3 md:p-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('messages.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </div>

        {/* Liste des conversations */}
        <div 
          className="flex-1 overflow-y-auto pb-16 md:pb-0"
        >
          {loading ? (
            <div className="animate-pulse p-4 space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gray-200 rounded-full" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              {t('messages.no_conversation_found')}
            </div>
          ) : (
            filteredConversations.map((conversation) => {
              const otherUser = getOtherParticipant(conversation);
              
              if (!otherUser) {
                console.warn('No other user found for conversation:', conversation);
                return null;
              }

              const currentUserParticipant = conversation.participants.find(
                p => p.user_id === user?.id
              );
              const hasUnread = currentUserParticipant?.unread_count > 0;

              return (
                <div key={conversation.id} className="relative group">
                  <Link
                    to={getPathForLang(`/messages/${conversation.id}`)}
                    className={`flex items-center gap-2 md:gap-3 p-3 md:p-4 hover:bg-gray-50 relative ${
                      hasUnread ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="relative group/avatar">
                      <Avatar 
                        url={otherUser.avatar_url} 
                        username={otherUser.username}
                        size="md"
                      />
                      <Link
                        to={getPathForLang(`/users/${otherUser.username}`)}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-30 rounded-full transition-all duration-200 opacity-0 group-hover/avatar:opacity-100"
                        title={t('messages.conversation.view_profile')}
                      >
                        <Users className="w-5 h-5 text-white" />
                      </Link>
                    </div>
                    <div className="flex-1 min-w-0 pr-8">
                      <div className="flex justify-between items-baseline gap-2">
                        <h3 className="font-medium text-gray-900 truncate text-sm md:text-base">
                          {otherUser.username}
                        </h3>
                        {conversation.last_message && (
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {new Date(conversation.last_message.created_at).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-gray-600 truncate">
                          {conversation.last_message?.content || t('messages.no_message')}
                        </p>
                        {hasUnread && (
                          <span className="flex-shrink-0 w-2 h-2 bg-primary-500 rounded-full" />
                        )}
                      </div>
                    </div>
                  </Link>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpen(menuOpen === conversation.id ? null : conversation.id);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <MoreVertical className="w-5 h-5" />
                  </button>
                  
                  {menuOpen === conversation.id && (
                    <div className="absolute right-2 top-[calc(50%+1.5rem)] mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                      <Link
                        to={getPathForLang(`/users/${otherUser.username}`)}
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpen(null);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <Users className="w-4 h-4" />
                        {t('messages.conversation.view_profile')}
                      </Link>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setConversationToDelete(conversation.id);
                          setMenuOpen(null);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        {t('messages.delete_conversation')}
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Modal avec padding supplémentaire */}
      <Modal
        isOpen={!!conversationToDelete}
        onClose={() => setConversationToDelete(null)}
        title={t('messages.delete_conversation')}
        actions={
          <>
            <button
              onClick={() => deleteConversation(conversationToDelete!)}
              className="inline-flex justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
            >
              {t('messages.delete')}
            </button>
            <button
              onClick={() => setConversationToDelete(null)}
              className="inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              {t('messages.cancel')}
            </button>
          </>
        }
        className="z-[1000]"
      >
        <p className="text-sm text-gray-500">
          {t('messages.confirm_delete')}
        </p>
      </Modal>


    </>
  );
}; 